import axios from 'axios'
import { useState } from 'react'
import { Alert } from '../components/common/Alert'
import { DOC_MEDIA_UPLOAD_PATH, DOCS_MESSAGES } from '../components/docs/docs.constants'
import { getToken } from '../utils'
import { UploadedDocMedia } from '../interfaceTypes/docs'

/**
 * Uploads inline doc media to the dedicated multipart endpoint and returns the
 * created asset (S3 key + presigned URL). Independent of the case/user upload
 * flows by design.
 */
export const useDocMediaUpload = (pageId?: string) => {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)

  const uploadMedia = async (file: File): Promise<UploadedDocMedia | null> => {
    if (!file) return null
    setUploading(true)
    setProgress(0)
    try {
      const formData = new FormData()
      formData.append('file', file)
      if (pageId) formData.append('pageId', pageId)

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}${DOC_MEDIA_UPLOAD_PATH}`,
        formData,
        {
          headers: { authorization: `Bearer ${getToken()}` },
          onUploadProgress: (event) => {
            if (event.total) setProgress(Math.round((event.loaded * 100) / event.total))
          },
        },
      )

      const asset = data?.asset
      if (!asset?.url) {
        Alert.error(DOCS_MESSAGES.UPLOAD_ERROR)
        return null
      }
      return {
        id: asset.id,
        key: asset.key,
        url: asset.url,
        mediaType: asset.mediaType,
        fileName: asset.fileName,
      }
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message || (error as any)?.message || DOCS_MESSAGES.UPLOAD_ERROR
      Alert.error(message)
      return null
    } finally {
      setUploading(false)
    }
  }

  return { uploadMedia, uploading, progress }
}
